<script setup>
import { computed } from 'vue';

import { HrBtn } from '../primitives';

/**
 * Outlined secondary button for supporting or secondary-emphasis actions.
 *
 * @status recommended
 * @group actions
 * @useWhen Secondary-priority actions that need visible but not primary-level emphasis
 * @related HrPageActionSecondaryBtn
 * @related HrSubtleBtn
 * @a11y Native button; activates on Enter and Space.
 */
defineOptions({ name: 'HrSupportingBtn' });

const emit = defineEmits({
  /** Fired when the user activates the button. */
  click: null,
});

const props = defineProps({
  /**
   * Button text label. When null the default slot provides the content.
   * @replacesSlot default
   */
  label: {
    type: String,
    default: null,
  },
  /** Icon displayed to the left of the label. */
  icon: {
    type: String,
    default: null,
  },
  /** Shows a loading spinner in place of the label. */
  loading: {
    type: Boolean,
    default: false,
  },
  /** Prevents interaction and applies a dimmed appearance. */
  disable: {
    type: Boolean,
    default: false,
  },
  /** Renders a pill-shaped skeleton placeholder instead of the button. */
  pending: {
    type: Boolean,
    default: false,
  },
  /** Vue Router route location. Renders the button as a router-link. */
  to: {
    type: [String, Object],
    default: null,
  },
  /** Replace the current history entry instead of pushing a new one. */
  replace: {
    type: Boolean,
    default: false,
  },
  /** Native anchor href. Use for external links; prefer `to` for in-app navigation. */
  href: {
    type: String,
    default: null,
  },
  /** Native anchor target (e.g. `_blank`). Only meaningful when `href` is set. */
  target: {
    type: String,
    default: null,
  },
  /**
   * Additional Quasar props forwarded to q-btn in legacy-theme mode.
   * @deprecated Remove once the legacy theme is removed.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

// @todo Remove legacy-props when Legacy theme is removed
const mergedLegacyProps = computed(() => ({
  flat: true,
  color: 'primary',
  ...props.legacyProps,
}));
</script>
<template>
  <hr-btn
    variant="outlined"
    v-bind="$props"
    :legacy-props="mergedLegacyProps"
    @click="emit('click', $event)"
  >
    <!-- @slot Button label / content. -->
    <slot />
  </hr-btn>
</template>
